import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { 
  ChefHat, Clock, CheckCircle, ArrowLeft, Volume2, VolumeX, RefreshCw
} from 'lucide-react';
import { playHotelBellSound } from '../../hooks/useOrderNotifications';
import useSmartPolling from '../../hooks/useSmartPolling';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const KitchenPanel = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(true);
  // Use a ref so fetchOrders stays stable and the polling interval is not
  // torn down + recreated on every state update.
  const lastOrderCountRef = useRef(0);
  const soundEnabledRef = useRef(true);
  useEffect(() => { soundEnabledRef.current = soundEnabled; }, [soundEnabled]);

  const fetchOrders = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/pedidos/cocina`);
      if (response.ok) {
        const data = await response.json();
        const pendingCount = data.filter(o => o.estado === 'pendiente').length;
        if (pendingCount > lastOrderCountRef.current && lastOrderCountRef.current > 0 && soundEnabledRef.current) {
          playHotelBellSound();
        }
        lastOrderCountRef.current = pendingCount;
        setOrders(data);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useSmartPolling(fetchOrders, 10000);

  const updateOrderStatus = async (orderId, newStatus) => {
    const token = localStorage.getItem('admin_token');
    try {
      // Find the order to get its items
      const order = [...pendingOrders, ...preparingOrders, ...readyOrders].find(o => o.id === orderId);

      if (order && order.items) {
        // Update each item in PARALLEL (was sequential await — caused noticeable
        // freeze on large orders since every item was a separate round-trip).
        const updates = order.items
          .map((item, i) => ({
            idx: item.original_index !== undefined ? item.original_index : i,
            estado: item.estado,
          }))
          .filter(it => it.estado !== newStatus && it.estado !== 'entregado')
          .map(it => fetch(`${API_URL}/api/pedidos/${orderId}/item/${it.idx}/estado?status=${newStatus}&source=cocina`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          }));
        await Promise.all(updates);
      }

      fetchOrders();
    } catch (error) {
      console.error('Error updating order:', error);
    }
  };

  const getStatusColor = (estado) => {
    switch (estado) {
      case 'pendiente': return 'bg-red-500';
      case 'preparando': return 'bg-amber-500';
      case 'listo': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusLabel = (estado) => {
    switch (estado) {
      case 'pendiente': return 'NUEVO';
      case 'preparando': return 'EN PREP.';
      case 'listo': return 'LISTO';
      default: return estado;
    }
  };

  const getTimeAgo = (createdAt) => {
    const now = new Date();
    const created = new Date(createdAt);
    const diffMs = now - created;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Ahora';
    if (diffMins < 60) return `${diffMins} min`;
    return `${Math.floor(diffMins / 60)}h ${diffMins % 60}m`;
  };

  // Filter orders based on KITCHEN ITEMS status, not overall order status
  const getKitchenItemsStatus = (order) => {
    const items = order.items || [];
    if (items.length === 0) return 'none';
    
    const allReady = items.every(item => item.estado === 'listo' || item.estado === 'entregado');
    const allDelivered = items.every(item => item.estado === 'entregado');
    const anyPreparing = items.some(item => item.estado === 'preparando');
    
    if (allDelivered) return 'entregado';
    if (allReady) return 'listo';
    if (anyPreparing) return 'preparando';
    return 'pendiente';
  };

  const pendingOrders = orders.filter(o => getKitchenItemsStatus(o) === 'pendiente');
  const preparingOrders = orders.filter(o => getKitchenItemsStatus(o) === 'preparando');
  const readyOrders = orders.filter(o => getKitchenItemsStatus(o) === 'listo');

  return (
    <div className="min-h-screen bg-stone-900 text-white p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => navigate('/admin')}
            className="p-2 hover:bg-stone-800 rounded-lg"
          >
            <ArrowLeft size={24} />
          </button>
          <div className="flex items-center gap-3">
            <ChefHat size={32} className="text-orange-500" />
            <div>
              <h1 className="text-2xl font-bold">COCINA</h1>
              <p className="text-stone-400 text-sm">Pedidos de comida</p>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <button
            onClick={() => setSoundEnabled(!soundEnabled)}
            className={`p-3 rounded-lg ${soundEnabled ? 'bg-green-600' : 'bg-stone-700'}`}
            title={soundEnabled ? 'Sonido activado' : 'Sonido desactivado'}
          >
            {soundEnabled ? <Volume2 size={20} /> : <VolumeX size={20} />}
          </button>
          <button
            onClick={fetchOrders}
            className="p-3 bg-stone-700 hover:bg-stone-600 rounded-lg"
          >
            <RefreshCw size={20} />
          </button>
          {pendingOrders.length > 0 && (
            <Badge className="bg-red-500 text-white text-lg px-3 py-1 animate-pulse">
              {pendingOrders.length} NUEVOS
            </Badge>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw size={32} className="animate-spin text-stone-500" />
        </div>
      ) : orders.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64 text-stone-500">
          <ChefHat size={64} className="mb-4 opacity-50" />
          <p className="text-xl">Sin pedidos pendientes</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* PENDIENTES */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse"></div>
              <h2 className="text-lg font-semibold text-red-400">PENDIENTES ({pendingOrders.length})</h2>
            </div>
            <div className="space-y-4">
              {pendingOrders.map(order => (
                <OrderCard 
                  key={order.id} 
                  order={order} 
                  onStatusChange={updateOrderStatus}
                  getTimeAgo={getTimeAgo}
                  getStatusColor={getStatusColor}
                  getStatusLabel={getStatusLabel}
                  nextStatus="preparando"
                  nextLabel="EMPEZAR"
                />
              ))}
            </div>
          </div>

          {/* EN PREPARACIÓN */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-3 h-3 rounded-full bg-amber-500"></div>
              <h2 className="text-lg font-semibold text-amber-400">EN PREPARACIÓN ({preparingOrders.length})</h2>
            </div>
            <div className="space-y-4">
              {preparingOrders.map(order => (
                <OrderCard 
                  key={order.id} 
                  order={order} 
                  onStatusChange={updateOrderStatus}
                  getTimeAgo={getTimeAgo}
                  getStatusColor={getStatusColor}
                  getStatusLabel={getStatusLabel}
                  nextStatus="listo"
                  nextLabel="LISTO"
                />
              ))}
            </div>
          </div>

          {/* LISTOS - Esperando al camarero */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <h2 className="text-lg font-semibold text-green-400">LISTOS - Esperando camarero ({readyOrders.length})</h2>
            </div>
            <div className="space-y-4">
              {readyOrders.map(order => (
                <Card key={order.id} className="bg-green-900/20 border-green-700">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-3xl font-bold text-white">#{order.mesa_numero}</span>
                        <Badge className="bg-green-500 text-white">LISTO</Badge>
                      </div>
                      <div className="flex items-center gap-1 text-stone-400">
                        <Clock size={14} />
                        <span className="text-sm">{getTimeAgo(order.created_at)}</span>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2 mb-4">
                      {order.items.map((item, idx) => (
                        <li key={idx} className="flex justify-between items-start">
                          <div>
                            <span className="text-white font-medium">{item.quantity}x {item.product_name}</span>
                            {item.options?.length > 0 && (
                              <p className="text-stone-400 text-sm">{item.options.join(', ')}</p>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                    <div className="text-center p-3 bg-green-800/30 rounded-lg border border-green-600/50">
                      <p className="text-green-300 font-medium">✓ Avisado a barra</p>
                      <p className="text-green-400/70 text-sm">El camarero lo marcará como servido</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const OrderCard = ({ order, onStatusChange, getTimeAgo, getStatusColor, getStatusLabel, nextStatus, nextLabel }) => {
  return (
    <Card className="bg-stone-800 border-stone-700">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-3xl font-bold text-white">#{order.mesa_numero}</span>
            <Badge className={`${getStatusColor(order.estado)} text-white`}>
              {getStatusLabel(order.estado)}
            </Badge>
          </div>
          <div className="flex items-center gap-1 text-stone-400">
            <Clock size={14} />
            <span className="text-sm">{getTimeAgo(order.created_at)}</span>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <ul className="space-y-2 mb-4">
          {order.items.map((item, idx) => (
            <li key={idx} className="flex justify-between items-start">
              <div>
                <span className="text-white font-medium">{item.quantity}x {item.product_name}</span>
                {item.options?.length > 0 && (
                  <p className="text-stone-400 text-sm">{item.options.join(', ')}</p>
                )}
                {item.notes && (
                  <p className="text-amber-400 text-sm">Nota: {item.notes}</p>
                )}
              </div>
            </li>
          ))}
        </ul>
        
        {order.notes && (
          <p className="text-amber-400 text-sm mb-3 p-2 bg-amber-900/30 rounded">
            📝 {order.notes}
          </p>
        )}
        
        <Button 
          onClick={() => onStatusChange(order.id, nextStatus)}
          className={`w-full ${
            nextStatus === 'preparando' ? 'bg-amber-600 hover:bg-amber-700' :
            nextStatus === 'listo' ? 'bg-green-600 hover:bg-green-700' :
            'bg-blue-600 hover:bg-blue-700'
          }`}
        >
          <CheckCircle size={18} className="mr-2" />
          {nextLabel}
        </Button>
      </CardContent>
    </Card>
  );
};

export default KitchenPanel;
